import React from 'react'

import { useDesignMode } from 'src/design-mode/useDesignMode'

import { Menu as AntdSeamMenu } from './Menu.antd'
import {
  Menu as ShadcnSeamMenu,
  type MenuProps as SeamMenuProps,
} from './Menu.shadcn'

type AntdMenuRef = React.ElementRef<typeof AntdSeamMenu>
/** Either backend's ref target — antd's `MenuRef`, or the shadcn root `<ul>`. */
type MenuRef = AntdMenuRef | HTMLUListElement

export type MenuProps = SeamMenuProps

/**
 * Compat seam for antd's `Menu`.
 *
 * `Menu` was never ported before this: `./Menu.tsx` used to be a bare
 * `export { default as Menu } from 'antd/es/menu'` re-export, with no shadcn
 * half and no item styling of its own — the root cause of survey finding S5
 * (every seam Menu outside the sidebar rendered unpadded once the seam
 * existed, because the seam carried none). Fixed here by building the port
 * properly rather than patching padding onto the old re-export: see
 * `./Menu.shadcn.tsx`'s header for the shape and the styling approach.
 *
 * ---------------------------------------------------------------------------
 * THE SWITCH, and its safety property
 *
 * `useDesignMode()` returns `'antd'` for everyone who has not picked "New
 * design" under Theme — which is the shipped default, so it is every page load
 * of every customer who has not opted in. In the `'antd'` branch this component renders
 * `./Menu.antd` (antd's own Menu, unchanged) with the props it was given:
 * same element, same class names, same DOM. `./__tests__/menu-seam-parity.
 * test.tsx` asserts that byte for byte across a prop matrix, and it is the
 * test to keep green: `development` continuing to ship antd unchanged to
 * customers is the whole premise of shipping shadcn one component at a time.
 *
 * `sidebar` and `surface` are NOT part of antd's `MenuProps` — they are this
 * seam's own additions, read only by `./Menu.shadcn`. Both are stripped
 * before reaching the antd branch so an unrecognised prop can never change
 * antd's own output.
 * ------------------------------------------------------------------------- */
const MenuDispatcher = React.forwardRef<MenuRef, MenuProps>(
  ({ sidebar, surface, ...props }, ref) => {
    const designMode = useDesignMode()

    if (designMode === 'shadcn') {
      return (
        <ShadcnSeamMenu
          sidebar={sidebar}
          surface={surface}
          {...props}
          ref={ref as React.Ref<HTMLUListElement>}
        />
      )
    }

    return <AntdSeamMenu {...props} ref={ref as React.Ref<AntdMenuRef>} />
  }
)

MenuDispatcher.displayName = 'Menu'

/**
 * antd's statics are carried across EXPLICITLY, not by copying antd's
 * component object: `Object.assign(dispatcher, AntdSeamMenu)` would also copy
 * a forwardRef object's `$$typeof`/`render` and turn this into something React
 * cannot render (the same reasoning `./Button.tsx` spells out). They are
 * antd's own legacy children API and are unported — no call site here uses
 * them — but dropping them would silently degrade the seam versus the
 * `export { default } from 'antd/es/menu'` it replaced.
 */
export const Menu = Object.assign(MenuDispatcher, {
  Item: AntdSeamMenu.Item,
  SubMenu: AntdSeamMenu.SubMenu,
  ItemGroup: AntdSeamMenu.ItemGroup,
  Divider: AntdSeamMenu.Divider,
})

export default Menu
